'use client'

import 'swiper/css'

import clsx from 'clsx'
import { useTranslations } from 'next-intl'
import { useState } from 'react'
import type { Swiper as SwiperType } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'

import { ModelsNewsMediaItem } from '@/api/generated-api'
import TaskBackChevronLeftIcon from '@/assets/icons/task-back-chevron-left.svg'
import Image from '@/ui/MediaImage'

import S from './NewsMediaCarousel.module.scss'

type NewsMediaCarouselProps = {
  items: ModelsNewsMediaItem[]
  title: string
}

const NewsMediaCarousel = ({ items, title }: NewsMediaCarouselProps) => {
  const t = useTranslations('news.detail.carousel')
  const [swiper, setSwiper] = useState<SwiperType | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)

  if (items.length === 0) {
    return null
  }

  const isBeginning = activeIndex === 0
  const isEnd = activeIndex >= items.length - 1

  return (
    <div className={S.root}>
      <div className={S.viewport}>
        <Swiper
          className={S.swiper}
          slidesPerView={1}
          spaceBetween={0}
          onSwiper={setSwiper}
          onSlideChange={instance => setActiveIndex(instance.activeIndex)}
        >
          {items.map((item, index) => (
            <SwiperSlide key={item.media_id ?? `${item.url}-${index}`}>
              <div className={S.slide}>
                {item.url ? (
                  <Image
                    src={item.url}
                    alt={title}
                    fill
                    className={S.image}
                    sizes="(max-width: 768px) 100vw, 1000px"
                  />
                ) : null}
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
      {items.length > 1 ? (
        <div className={S.nav}>
          <button
            type="button"
            className={clsx(S.navButton, isBeginning && S.navButtonInactive)}
            aria-label={t('prevAria')}
            disabled={isBeginning}
            onClick={() => swiper?.slidePrev()}
          >
            <TaskBackChevronLeftIcon className={S.navIcon} aria-hidden />
          </button>
          <div className={S.dots} role="tablist" aria-label={t('dotsAria')}>
            {items.map((item, index) => (
              <button
                key={item.media_id ?? `${item.url}-${index}`}
                type="button"
                role="tab"
                aria-selected={index === activeIndex}
                aria-label={t('slideAria', { index: index + 1 })}
                className={`${S.dot} ${
                  index === activeIndex ? S.dotActive : ''
                }`}
                onClick={() => swiper?.slideTo(index)}
              />
            ))}
          </div>
          <button
            type="button"
            className={clsx(S.navButton, isEnd && S.navButtonInactive)}
            aria-label={t('nextAria')}
            disabled={isEnd}
            onClick={() => swiper?.slideNext()}
          >
            <TaskBackChevronLeftIcon
              className={clsx(S.navIcon, S.navIconRight)}
              aria-hidden
            />
          </button>
        </div>
      ) : null}
    </div>
  )
}

export default NewsMediaCarousel
